import { useCallback } from "react"
import type { MutableRefObject } from "react"
import type { QueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import {
	spliceErefIntoCache,
	spliceFurnitureIntoCache,
	removeFurnitureFromCache,
	setErefVersionInCache,
} from "helpers/quotationCache.helper"
import { buildBatchStatusFailureMessage, resolveBatchReviewToggle } from "helpers/batchFurniture.helper"
import AnnotationService from "services/annotations.service"
import {
	resolveCountertopSeriesAfterErefChange,
	resolveDeliveryDateAfterErefChange,
} from "components/ui/customWebviewer/FurnitureSidebar/furnitureSidebar.helpers"
import type { TBatchReviewToggle } from "helpers/batchFurniture.helper"
import type { TDeleteConfirmTarget } from "../quotationViewer.helpers"
import type { RootEntry } from "../core/shadowPortal"
import type { TViewerMode } from "store/viewer.store"
import type {
	useMoveFurnitureToEref,
	useDisconnectFurnitureFromEref,
	useBatchUpdateFurnitureStatus,
	useBatchDeleteFurniture,
	useUpdateFurnitureStatus,
} from "hooks/api/quotation/quotations.hook"
import type {
	TEcall,
	TEref,
	TErefWithFurniture,
	TFurniture,
	TFurnitureStatus,
	TPageElevationData,
	TConnectFurnitureResponse,
	TMoveFurnitureResponse,
	TBatchUpdateFurnitureStatusResponse,
	TBatchDeleteFurnitureResponse,
} from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import type { TAnnotationType } from "types/annotations.type"

// What the floating pill is currently acting on: a furniture selection (single or batch, REQ-369),
// exactly one eref (REQ-376), or exactly one ecall (REQ-374). Never more than one kind at once —
// syncSelectionToolbar resolves a single kind.
type TSelectionToolbar =
	| { kind: "furniture"; furniture: TFurniture[] }
	| { kind: "eref"; eref: TErefWithFurniture }
	| { kind: "ecall"; ecall: TEcall }
	| null
type TConnectModal = { mode: "connect" | "move"; furniture: TFurniture[]; erefs: TEref[] } | null
type TDeleteConfirm = TDeleteConfirmTarget | null

// REQ-369 batch selection toolbar: multi-select connect / move / detach / status / delete, plus
// the single-object quotation toggle. All state lives in the orchestrator; this hook owns the
// handlers and receives the stable refs / setters / mutations it needs. Behavior is byte-identical
// to the inline version — the batch flows chain optimistic-lock versions through the responses and
// repaint eref colors imperatively (see the reactive-annotations phase for the eventual rework).
export type BatchOperationsContext = {
	numericQuotationIdRef: MutableRefObject<number | undefined>
	relatedObjectsRef: MutableRefObject<TPageElevationData[] | undefined>
	selectionToolbarEntryRef: MutableRefObject<RootEntry | null>
	selectedFurnitureRef: MutableRefObject<TFurniture | undefined>
	modeRef: MutableRefObject<TViewerMode>
	setSelectionToolbar: (value: TSelectionToolbar) => void
	setConnectModal: (value: TConnectModal) => void
	setDeleteConfirm: (value: TDeleteConfirm) => void
	setBatchPending: (value: boolean) => void
	moveFurnitureAsync: ReturnType<typeof useMoveFurnitureToEref>["mutateAsync"]
	disconnectFurnitureAsync: ReturnType<typeof useDisconnectFurnitureFromEref>["mutateAsync"]
	batchStatusAsync: ReturnType<typeof useBatchUpdateFurnitureStatus>["mutateAsync"]
	updateFurnitureStatusAsync: ReturnType<typeof useUpdateFurnitureStatus>["mutateAsync"]
	batchDeleteAsync: ReturnType<typeof useBatchDeleteFurniture>["mutateAsync"]
	findFurnitureById: (furnitureId: number) => TFurniture | undefined
	findErefWithFurnitureById: (erefId: number) => TErefWithFurniture | undefined
	findEcallById: (ecallId: number) => TEcall | undefined
	handleFurnitureClose: () => void
	/**
	 * Post-delete cleanup owned by the orchestrator, for the panels this hook doesn't know about
	 * (the split screen's own sidebar). The primary right panel is closed here directly.
	 */
	onFurnitureDeleted: (furnitureIds: number[]) => void
	queryClient: QueryClient
	success: (text: string) => void
	error: (text: unknown) => void
	formatError: (error: unknown) => void
}

export const useBatchOperations = (ctx: BatchOperationsContext) => {
	const {
		numericQuotationIdRef,
		relatedObjectsRef,
		selectionToolbarEntryRef,
		selectedFurnitureRef,
		modeRef,
		setSelectionToolbar,
		setConnectModal,
		setDeleteConfirm,
		setBatchPending,
		moveFurnitureAsync,
		disconnectFurnitureAsync,
		batchStatusAsync,
		updateFurnitureStatusAsync,
		batchDeleteAsync,
		findFurnitureById,
		findErefWithFurnitureById,
		findEcallById,
		handleFurnitureClose,
		onFurnitureDeleted,
		queryClient,
		success,
		error,
		formatError,
	} = ctx

	const getSelectedFurniture = useCallback((): TFurniture[] => {
		return AnnotationService.getSelectedFurnitureIds()
			.map((id) => findFurnitureById(id))
			.filter((f): f is TFurniture => !!f)
	}, [findFurnitureById])

	// Position the toolbar overlay above the selection's screen bounding box, in
	// app-container-relative px (the pill is centered + lifted above via a CSS transform on the
	// container). Hides it when the selection isn't measurable (scrolled off / paging in).
	// `entityTypes` selects which highlights are measured — the eref pill anchors to the eref box,
	// not to the furniture that happens to be selected alongside nothing.
	const positionSelectionToolbar = useCallback(
		(entityTypes: TAnnotationType[] = ["furniture"]) => {
			const entry = selectionToolbarEntryRef.current
			const toolbarContainerParent = entry?.container.parentElement
			if (!entry || !toolbarContainerParent) return
			const bounds = AnnotationService.getSelectionScreenBounds(entityTypes)
			if (!bounds) {
				entry.container.style.display = "none"
				return
			}
			const parentRect = toolbarContainerParent.getBoundingClientRect()
			entry.container.style.left = `${(bounds.left + bounds.right) / 2 - parentRect.left}px`
			entry.container.style.top = `${bounds.top - parentRect.top}px`
			entry.container.style.display = "block"
		},
		[selectionToolbarEntryRef],
	)

	// Recompute what the toolbar acts on from the live Apryse selection: a furniture selection
	// (single or batch), exactly one eref, or exactly one ecall (REQ-374) and nothing else. Anything
	// else — no selection, a mixed-type selection, a multi-ecall selection, or a page box — shows no
	// pill (REQ-367). Called on every selection change and after each mutation.
	const syncSelectionToolbar = useCallback(() => {
		if (modeRef.current !== "editor") {
			setSelectionToolbar(null)
			return
		}
		const furniture = getSelectedFurniture()
		if (furniture.length > 0) {
			setSelectionToolbar({ kind: "furniture", furniture })
			return
		}
		const entities = AnnotationService.getSelectedEntities()
		if (entities.length === 1 && entities[0].entityType === "eref") {
			const eref = findErefWithFurnitureById(entities[0].entityId)
			setSelectionToolbar(eref ? { kind: "eref", eref } : null)
			return
		}
		if (entities.length === 1 && entities[0].entityType === "ecall") {
			const ecall = findEcallById(entities[0].entityId)
			setSelectionToolbar(ecall ? { kind: "ecall", ecall } : null)
			return
		}
		setSelectionToolbar(null)
	}, [modeRef, setSelectionToolbar, getSelectedFurniture, findErefWithFurnitureById, findEcallById])

	// Open the connect/move modal. One entry point: the mode ("connect" for a selection with any
	// unconnected object, "move" when all are already connected) drives only the modal wording —
	// the toolbar button is always "Connect to elevation".
	const openConnectModal = useCallback(
		(furniture: TFurniture[]) => {
			const pageNumber = furniture[0]?.page_number
			// Only erefs on the SAME page as the selected objects are valid targets (the connect
			// endpoint requires it, and erefs are page-bound). Filter a flat list by page_number so we
			// never surface erefs from other pages.
			const pageErefs = (relatedObjectsRef.current ?? [])
				.flatMap((p) => p.erefs)
				.filter((e) => e.page_number === pageNumber)
			const mode: "connect" | "move" = furniture.some((f) => f.eref_id == null) ? "connect" : "move"
			setConnectModal({ mode, furniture, erefs: pageErefs })
		},
		[relatedObjectsRef, setConnectModal],
	)

	// Connect (unconnected → target) and Move (connected → different target) share one flow, handled
	// by a single transactional `move` endpoint that reassigns every selected furniture to the target
	// eref regardless of its current link. The response carries authoritative new versions for the
	// target eref, every source eref that lost furniture, and every requested furniture (no-ops
	// included) — no per-request version chaining or optimistic +1 fallbacks needed.
	//
	// Resolves whether the request succeeded, so a caller that owns UI beyond the modal can keep it
	// open on failure (the eref connect-objects mode, REQ-372, stays up for a retry).
	const runConnectOrMove = useCallback(
		async (targetErefId: number, furniture: TFurniture[]): Promise<boolean> => {
			const qid = numericQuotationIdRef.current
			if (qid == null || Number.isNaN(qid)) return false
			const allErefs = relatedObjectsRef.current?.flatMap((p) => p.erefs) ?? []
			const targetEref = allErefs.find((e) => e.id === targetErefId)
			if (!targetEref) {
				// The chosen elevation vanished from the cache between opening the modal and confirming
				// (e.g. a background refetch). Tell the user instead of silently doing nothing.
				setConnectModal(null)
				error("That elevation is no longer available")
				return false
			}

			setBatchPending(true)
			// Source erefs currently holding a moved item (per the LIVE cache) — recolored afterwards if
			// they end up empty, and version-bumped in the cache. `wasMove` (any item currently on a
			// different eref) drives only the toast. `anyActualMove` also counts unconnected → target
			// (unlinked→linked is a move server-side), which is when the target eref version is bumped.
			const sourceErefIds = new Set(
				furniture
					.map((f) => (findFurnitureById(f.id) ?? f).eref_id)
					.filter((id): id is number => id != null && id !== targetErefId),
			)
			const wasMove = sourceErefIds.size > 0
			const anyActualMove = furniture.some((f) => (findFurnitureById(f.id) ?? f).eref_id !== targetErefId)
			// Live-cache eref versions, used as the +1 fallback base when the response omits an eref.
			const erefVersionById = new Map(allErefs.map((e) => [e.id, e.version]))
			try {
				const resp = (
					await moveFurnitureAsync({
						quotationId: qid,
						erefId: targetErefId,
						body: {
							// Versions come from the LIVE cache, not the possibly-stale modal snapshot.
							eref_version: targetEref.version,
							furniture: furniture.map((f) => ({ id: f.id, version: (findFurnitureById(f.id) ?? f).version })),
						},
					})
				)?.data as TMoveFurnitureResponse | undefined

				// Prefer the authoritative response versions; fall back to an optimistic +1 bump for any
				// entity the response omitted (a 2xx with a partial/empty body still updates the cache).
				const respFurnitureMap = new Map((resp?.furniture ?? []).map((f) => [f.id, f.version]))
				const respErefMap = new Map((resp?.erefs ?? []).map((e) => [e.id, e.version]))
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", qid], (cached) => {
					let next = cached
					// Every requested furniture now lives on the target eref. Moved items bump +1 on
					// fallback; no-ops (already on the target) keep their version when the response omits them.
					for (const f of furniture) {
						const current = findFurnitureById(f.id) ?? f
						const fallback = current.eref_id === targetErefId ? current.version : current.version + 1
						const version = respFurnitureMap.get(f.id) ?? fallback
						next = spliceFurnitureIntoCache(next, {
							id: f.id,
							version,
							eref_id: targetErefId,
							// A furniture already following its (old) eref's date now follows the target
							// eref's instead; an explicit override survives the move untouched.
							delivery_date: resolveDeliveryDateAfterErefChange(current.delivery_date ?? null, targetEref),
							countertop_series_and_colors: resolveCountertopSeriesAfterErefChange(
								current.countertop_series_and_colors ?? null,
								targetEref,
							),
						})
					}
					// Target eref: bumped once when at least one furniture actually moved onto it.
					if (respErefMap.has(targetErefId)) {
						next = setErefVersionInCache(next, targetErefId, respErefMap.get(targetErefId) as number)
					} else if (anyActualMove) {
						next = setErefVersionInCache(next, targetErefId, targetEref.version + 1)
					}
					// Source erefs: each lost at least one furniture, so each is bumped once.
					for (const oldErefId of sourceErefIds) {
						const version = respErefMap.get(oldErefId) ?? (erefVersionById.get(oldErefId) ?? 0) + 1
						next = setErefVersionInCache(next, oldErefId, version)
					}
					return next
				})

				// Recolor the target (now connected) and any source erefs that lost all their furniture.
				const fresh = (
					queryClient.getQueryData(["quotation", qid]) as AxiosResponse<TQuotationDetail> | undefined
				)?.data.related_objects
				AnnotationService.updateErefAnnotationStatus(targetErefId, "connected")
				for (const oldErefId of sourceErefIds) {
					const stillConnected = fresh?.some((p) => p.furniture.some((f) => f.eref_id === oldErefId))
					AnnotationService.updateErefAnnotationStatus(oldErefId, stillConnected ? "connected" : "unconnected")
				}

				setConnectModal(null)
				AnnotationService.deselectAll()
				setSelectionToolbar(null)
				const noun = furniture.length > 1 ? "Objects" : "Object"
				success(wasMove ? `${noun} moved to elevation` : `${noun} connected to elevation`)
				return true
			} catch (e) {
				formatError(e)
				queryClient.invalidateQueries({ queryKey: ["quotation", qid] })
				return false
			} finally {
				setBatchPending(false)
			}
		},
		[
			numericQuotationIdRef,
			relatedObjectsRef,
			setConnectModal,
			setBatchPending,
			setSelectionToolbar,
			moveFurnitureAsync,
			findFurnitureById,
			queryClient,
			success,
			error,
			formatError,
		],
	)

	const runDetach = useCallback(
		async (furniture: TFurniture[]) => {
			const qid = numericQuotationIdRef.current
			if (qid == null || Number.isNaN(qid)) return
			const allErefs = relatedObjectsRef.current?.flatMap((p) => p.erefs) ?? []
			setBatchPending(true)
			// Seed versions from the LIVE cache, not the stale selection snapshot.
			const versionById = new Map(furniture.map((f) => [f.id, (findFurnitureById(f.id) ?? f).version]))
			const erefVersionById = new Map(allErefs.map((e) => [e.id, e.version]))
			try {
				const groups = new Map<number, TFurniture[]>()
				for (const f of furniture) {
					if (f.eref_id == null) continue
					const arr = groups.get(f.eref_id) ?? []
					arr.push(f)
					groups.set(f.eref_id, arr)
				}
				for (const [oldErefId, group] of groups) {
					const oldV = erefVersionById.get(oldErefId)
					if (oldV == null) continue // eref not in the live snapshot — can't safely version the request
					const resp = (
						await disconnectFurnitureAsync({
							quotationId: qid,
							erefId: oldErefId,
							body: {
								eref_version: oldV,
								furniture: group.map((f) => ({ id: f.id, version: versionById.get(f.id) as number })),
							},
						})
					)?.data as TConnectFurnitureResponse | undefined
					const newErefV = resp?.eref_version ?? oldV + 1
					// The disconnect response returns the detached furniture with authoritative new
					// versions — use those (fall back to +1 only if absent). Any leftover response
					// entries are furniture still on the eref; apply their versions, keeping eref_id.
					const respMap = new Map((resp?.furniture ?? []).map((x) => [x.id, x.version]))
					const updates: (Partial<TFurniture> & { id: number })[] = []
					for (const f of group) {
						const nv = respMap.get(f.id) ?? (versionById.get(f.id) as number) + 1
						respMap.delete(f.id)
						versionById.set(f.id, nv)
						// Detaching drops eref_id, so a furniture that was only inheriting its
						// elevation's date has nothing left to inherit from — recompute delivery_date
						// from the LIVE cache (an explicit override survives untouched; an inherited
						// one clears to null) instead of leaving the pre-detach value stale.
						const liveFurniture = findFurnitureById(f.id) ?? f
						const liveDeliveryDate = liveFurniture.delivery_date ?? null
						const liveCountertopSeries = liveFurniture.countertop_series_and_colors ?? null
						updates.push({
							id: f.id,
							version: nv,
							eref_id: null,
							delivery_date: resolveDeliveryDateAfterErefChange(liveDeliveryDate, undefined),
							countertop_series_and_colors: resolveCountertopSeriesAfterErefChange(liveCountertopSeries, undefined),
						})
					}
					for (const [id, version] of respMap) {
						versionById.set(id, version)
						updates.push({ id, version })
					}
					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", qid], (cached) => {
						let next = cached
						for (const u of updates) next = spliceFurnitureIntoCache(next, u)
						return setErefVersionInCache(next, oldErefId, newErefV)
					})
				}
				const fresh = (
					queryClient.getQueryData(["quotation", qid]) as AxiosResponse<TQuotationDetail> | undefined
				)?.data.related_objects
				for (const oldErefId of groups.keys()) {
					const stillConnected = fresh?.some((p) => p.furniture.some((f) => f.eref_id === oldErefId))
					AnnotationService.updateErefAnnotationStatus(oldErefId, stillConnected ? "connected" : "unconnected")
				}
				AnnotationService.deselectAll()
				setSelectionToolbar(null)
				success(`${furniture.length > 1 ? "Objects" : "Object"} detached from elevation`)
			} catch (e) {
				formatError(e)
				queryClient.invalidateQueries({ queryKey: ["quotation", qid] })
			} finally {
				setBatchPending(false)
			}
		},
		[
			numericQuotationIdRef,
			relatedObjectsRef,
			setBatchPending,
			setSelectionToolbar,
			disconnectFurnitureAsync,
			findFurnitureById,
			queryClient,
			success,
			formatError,
		],
	)

	// `in_quotation` is accepted here too (REQ-403's batch footer sends it): it is the only status
	// with preconditions, so it is also the only one that can come back with a `failed` map naming
	// the items that did not meet them. Those items were left untouched, so the toast has to say so
	// rather than reporting a clean success.
	const runBatchStatus = useCallback(
		async (furniture: TFurniture[], status: TFurnitureStatus) => {
			const qid = numericQuotationIdRef.current
			if (qid == null || Number.isNaN(qid)) return
			setBatchPending(true)
			try {
				const resp = (
					await batchStatusAsync({
						quotationId: qid,
						// Use live cache versions, not the stale selection snapshot.
						body: {
							status,
							furniture: furniture.map((f) => ({ id: f.id, version: (findFurnitureById(f.id) ?? f).version })),
						},
					})
				)?.data as TBatchUpdateFurnitureStatusResponse | undefined
				const items = resp?.furniture ?? []
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", qid], (cached) => {
					let next = cached
					for (const it of items) {
						next = spliceFurnitureIntoCache(next, { id: it.id, status: it.status, version: it.version })
					}
					return next
				})
				for (const it of items) AnnotationService.updateFurnitureEntityStatus(it.id, it.status)
				const failureMessage = buildBatchStatusFailureMessage(resp?.failed)
				if (failureMessage) {
					error(failureMessage)
					return
				}
				success(
					status === "in_quotation"
						? "Objects added to quotation"
						: status === "reviewed"
							? "Objects marked as reviewed"
							: "Objects marked as need review",
				)
			} catch (e) {
				formatError(e)
				queryClient.invalidateQueries({ queryKey: ["quotation", qid] })
			} finally {
				setBatchPending(false)
			}
		},
		[
			numericQuotationIdRef,
			setBatchPending,
			batchStatusAsync,
			findFurnitureById,
			queryClient,
			success,
			error,
			formatError,
		],
	)

	// Single-object quotation toggle (mirrors the right sidebar). Uses the single-furniture status
	// endpoint because this path acts on exactly ONE object, and that endpoint reports the per-object
	// precondition failure directly instead of the batch endpoint's 207 partial-success envelope.
	// (The batch endpoint does accept `in_quotation` — `runBatchStatus` above sends it.) `next` is
	// "in_quotation" (Add to quotation) or "reviewed" (Remove from quotation).
	const runSingleFurnitureStatus = useCallback(
		async (item: TFurniture, next: "in_quotation" | "reviewed") => {
			const qid = numericQuotationIdRef.current
			if (qid == null || Number.isNaN(qid)) return
			setBatchPending(true)
			try {
				const resp = await updateFurnitureStatusAsync({
					quotationId: qid,
					furnitureId: item.id,
					// Use the live cache version, not the stale selection snapshot.
					body: { version: (findFurnitureById(item.id) ?? item).version, status: next },
				})
				const updated = resp?.data as TFurniture | undefined
				if (updated) {
					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", qid], (cached) =>
						spliceFurnitureIntoCache(cached, updated),
					)
					AnnotationService.updateFurnitureEntityStatus(item.id, next)
				}
				success(next === "in_quotation" ? "Object added to quotation" : "Object removed from quotation")
			} catch (e) {
				formatError(e)
				queryClient.invalidateQueries({ queryKey: ["quotation", qid] })
			} finally {
				setBatchPending(false)
			}
		},
		[
			numericQuotationIdRef,
			setBatchPending,
			updateFurnitureStatusAsync,
			findFurnitureById,
			queryClient,
			success,
			formatError,
		],
	)

	const runBatchDelete = useCallback(
		async (furniture: TFurniture[]) => {
			const qid = numericQuotationIdRef.current
			if (qid == null || Number.isNaN(qid)) return
			setBatchPending(true)
			try {
				const ids = furniture.map((f) => f.id)
				// Use live cache versions, not the stale selection snapshot.
				const items = furniture.map((f) => `${f.id}:${(findFurnitureById(f.id) ?? f).version}`)
				const resp = (await batchDeleteAsync({ quotationId: qid, items }))?.data as
					| TBatchDeleteFurnitureResponse
					| undefined
				const updatedErefs = resp?.updated_erefs ?? []
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", qid], (cached) => {
					let next = removeFurnitureFromCache(cached, ids)
					for (const eref of updatedErefs) next = spliceErefIntoCache(next, eref)
					return next
				})
				AnnotationService.removeFurnitureAnnotations(ids)
				const fresh = (
					queryClient.getQueryData(["quotation", qid]) as AxiosResponse<TQuotationDetail> | undefined
				)?.data.related_objects
				for (const eref of updatedErefs) {
					const stillConnected = fresh?.some((p) => p.furniture.some((f) => f.eref_id === eref.id))
					AnnotationService.updateErefAnnotationStatus(eref.id, stillConnected ? "connected" : "unconnected")
				}
				// If the object whose sidebar is open was just deleted, close the now-stale panel.
				const openId = selectedFurnitureRef.current?.id
				if (openId != null && ids.includes(openId)) handleFurnitureClose()
				onFurnitureDeleted(ids)
				AnnotationService.deselectAll()
				setSelectionToolbar(null)
				setDeleteConfirm(null)
				success(ids.length > 1 ? "Objects deleted" : "Object deleted")
			} catch (e) {
				formatError(e)
				queryClient.invalidateQueries({ queryKey: ["quotation", qid] })
			} finally {
				setBatchPending(false)
			}
		},
		[
			numericQuotationIdRef,
			selectedFurnitureRef,
			setBatchPending,
			setSelectionToolbar,
			setDeleteConfirm,
			batchDeleteAsync,
			findFurnitureById,
			handleFurnitureClose,
			onFurnitureDeleted,
			queryClient,
			success,
			formatError,
		],
	)

	// The review toggle for a selection. The rule itself lives in `resolveBatchReviewToggle` because
	// the REQ-403 batch edit panel's footer has to reach exactly the same verdict — see the helper for
	// why a mixed or uniformly `in_quotation` selection offers no toggle at all.
	const getReviewToggle = useCallback(
		(furniture: TFurniture[]): TBatchReviewToggle | null => resolveBatchReviewToggle(furniture),
		[],
	)

	return {
		getSelectedFurniture,
		positionSelectionToolbar,
		syncSelectionToolbar,
		openConnectModal,
		runConnectOrMove,
		runDetach,
		runBatchStatus,
		runSingleFurnitureStatus,
		runBatchDelete,
		getReviewToggle,
	}
}
